import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

/**
 * ProductGallery
 * - Main image with click-to-zoom lightbox
 * - Thumbnail strip beneath the main image (main photo + admin-uploaded gallery)
 * - Fullscreen lightbox with previous/next, ESC to close, +/- zoom (up to 3x), scroll wheel to zoom
 */
export default function ProductGallery({ images, alt }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoom, setZoom] = useState(1);

  const list = Array.isArray(images) ? images.filter(Boolean) : [];

  useEffect(() => {
    // reset active when list changes and index out of range
    if (activeIdx >= list.length) setActiveIdx(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.length]);

  const prev = useCallback(() => {
    setActiveIdx((i) => (i - 1 + list.length) % list.length);
    setZoom(1);
  }, [list.length]);
  const next = useCallback(() => {
    setActiveIdx((i) => (i + 1) % list.length);
    setZoom(1);
  }, [list.length]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxOpen(false);
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(3, z + 0.25));
      else if (e.key === "-") setZoom((z) => Math.max(1, z - 0.25));
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxOpen, prev, next]);

  if (list.length === 0) return null;

  const activeSrc = list[activeIdx];

  return (
    <>
      {/* Main image */}
      <div className="relative">
        <div className="absolute inset-0 bg-blue-500/10 blur-[120px] rounded-full pointer-events-none" />
        <button
          type="button"
          onClick={() => { setLightboxOpen(true); setZoom(1); }}
          data-testid="product-photo-zoom"
          aria-label="Zoom photo"
          className="relative group bg-[#0A0F1A] border border-white/5 hover:border-[#39FF14]/40 rounded-2xl overflow-hidden aspect-square w-full flex items-center justify-center transition"
        >
          <img
            src={activeSrc}
            alt={alt}
            data-testid="product-photo"
            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
          />
          <span className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full bg-black/80 backdrop-blur border border-white/10 px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold text-zinc-300 opacity-0 group-hover:opacity-100 transition">
            <ZoomIn className="w-3 h-3 text-[#39FF14]" /> Zoom
          </span>
        </button>
      </div>

      {/* Thumbnail strip */}
      {list.length > 1 && (
        <div
          className="mt-3 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1"
          data-testid="product-thumbs"
        >
          {list.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              onClick={() => setActiveIdx(i)}
              data-testid="product-thumb"
              className={`shrink-0 w-16 h-16 rounded-md overflow-hidden border transition bg-black ${
                i === activeIdx
                  ? "border-[#39FF14] shadow-[0_0_12px_rgba(57,255,20,0.4)]"
                  : "border-white/10 hover:border-white/30"
              }`}
              aria-label={`Show image ${i + 1}`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col"
          role="dialog"
          aria-modal="true"
          data-testid="product-lightbox"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-400 font-bold">
              {activeIdx + 1} / {list.length}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setZoom((z) => Math.max(1, z - 0.25))}
                data-testid="lightbox-zoom-out"
                aria-label="Zoom out"
                className="w-9 h-9 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-zinc-300"
                disabled={zoom <= 1}
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="w-14 text-center text-xs text-zinc-500 tabular-nums">{Math.round(zoom * 100)}%</span>
              <button
                type="button"
                onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
                data-testid="lightbox-zoom-in"
                aria-label="Zoom in"
                className="w-9 h-9 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-zinc-300"
                disabled={zoom >= 3}
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setLightboxOpen(false)}
                data-testid="lightbox-close"
                aria-label="Close"
                className="ml-1 w-9 h-9 rounded-md bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/30 flex items-center justify-center text-zinc-300 hover:text-red-400 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Stage */}
          <div
            className="relative flex-1 overflow-auto flex items-center justify-center p-4"
            onWheel={(e) => {
              e.preventDefault();
              const delta = e.deltaY < 0 ? 0.15 : -0.15;
              setZoom((z) => Math.min(3, Math.max(1, +(z + delta).toFixed(2))));
            }}
            onClick={(e) => {
              // click on empty stage closes lightbox
              if (e.target === e.currentTarget) setLightboxOpen(false);
            }}
          >
            <img
              src={activeSrc}
              alt={alt}
              className="select-none max-w-full max-h-full object-contain transition-transform duration-200 cursor-zoom-in"
              style={{ transform: `scale(${zoom})`, transformOrigin: "center center" }}
              draggable="false"
              onDoubleClick={() => setZoom((z) => (z >= 2 ? 1 : z + 1))}
            />

            {list.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={prev}
                  aria-label="Previous image"
                  data-testid="lightbox-prev"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 flex items-center justify-center text-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={next}
                  aria-label="Next image"
                  data-testid="lightbox-next"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 flex items-center justify-center text-white"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>

          {/* Bottom thumbnails inside lightbox */}
          {list.length > 1 && (
            <div className="border-t border-white/5 px-4 py-2 flex gap-2 overflow-x-auto">
              {list.map((src, i) => (
                <button
                  key={`lb-${i}`}
                  type="button"
                  onClick={() => { setActiveIdx(i); setZoom(1); }}
                  className={`shrink-0 w-14 h-14 rounded-md overflow-hidden border transition bg-black ${
                    i === activeIdx
                      ? "border-[#39FF14] shadow-[0_0_10px_rgba(57,255,20,0.4)]"
                      : "border-white/10 hover:border-white/30 opacity-70 hover:opacity-100"
                  }`}
                  aria-label={`Show image ${i + 1}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
